import Box from "@mui/material/Box"
import type {
    AutoFrameI,
    EllipseI,
    FlexFrameI,
    FrameI,
    ImageI,
    ItemI,
    SelectInputT,
    SequenceI,
    SimpleStyleT,
    TickerI,
} from "tweenly"
import { getStyleFieldLabel } from "../../../../helpers/properties.helpers"
import ExpandableSection from "../../../common/ExpandableSection"
import ColorField from "../../../common/PropertyTools/ColorField"
import CompactSelect from "../../../common/PropertyTools/CompactSelect"
import PrefixedInput from "../../../common/PropertyTools/PrefixedInput"
import PropertyRow from "../../../common/PropertyTools/PropertyRow"

export type ColorEventT = {
    target: { name: string; value: any }
}

interface Props {
    object: ItemI | EllipseI | ImageI | SequenceI | TickerI | FrameI | FlexFrameI | AutoFrameI
    handleSetValue: (property: any, value: any) => void
}

/**
 * Figma calls box-sizing the stroke's position: with `border-box` the border is drawn inside
 * the object's size, with `content-box` outside it. Only the labels change — the stored value
 * is still the CSS one.
 */
const POSITION_OPTIONS = [
    { value: "border-box", label: "Inside" },
    { value: "content-box", label: "Outside" },
]

/** The border, laid out like Figma's stroke: color, then weight | style, then position. */
const Border = ({ object, handleSetValue }: Props) => {
    const findStyle = (property: string): SimpleStyleT | undefined =>
        object.styles.find((style) => style.property === property)
    const borderWidth = findStyle("borderWidth")
    const borderStyle = findStyle("borderStyle")
    const borderColor = findStyle("borderColor")
    const boxSizing = findStyle("boxSizing")

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement> | ColorEventT) =>
        handleSetValue(e.target.name, e.target.value)

    // Don't render if no border properties exist
    if (!borderWidth && !borderStyle && !borderColor && !boxSizing) return <></>

    const styleOptions = borderStyle
        ? (borderStyle.input as SelectInputT).props.map((value) => ({
              value,
              label: value.charAt(0).toUpperCase() + value.slice(1),
          }))
        : []

    return (
        <ExpandableSection
            expanded={false}
            title={"Border"}
            component={
                <Box display="flex" flexDirection="column" gap={1.5} width="100%" pb={0.5}>
                    {borderColor && (
                        <PropertyRow>
                            <ColorField
                                styleProperty={borderColor}
                                handleOnChange={handleOnChange}
                            />
                        </PropertyRow>
                    )}

                    {(borderWidth || borderStyle) && (
                        <PropertyRow>
                            <Box display="flex" gap={1}>
                                <Box flex={1} minWidth={0}>
                                    {borderWidth && (
                                        <PrefixedInput
                                            name="borderWidth"
                                            value={borderWidth.value}
                                            label={getStyleFieldLabel(borderWidth)}
                                            tooltip={`Border weight (${borderWidth.unit ?? "px"})`}
                                            handleOnChange={handleOnChange}
                                            min={0}
                                        />
                                    )}
                                </Box>
                                <Box flex={1} minWidth={0}>
                                    {borderStyle && (
                                        <CompactSelect
                                            name="borderStyle"
                                            value={String(borderStyle.value)}
                                            options={styleOptions}
                                            label={borderStyle.input.label ?? "border style"}
                                            tooltip="Border style"
                                            handleOnChange={handleOnChange}
                                        />
                                    )}
                                </Box>
                            </Box>
                        </PropertyRow>
                    )}

                    {boxSizing && (
                        <PropertyRow>
                            <Box display="flex" gap={1}>
                                <Box flex={1} minWidth={0}>
                                    <CompactSelect
                                        name="boxSizing"
                                        value={String(boxSizing.value)}
                                        options={POSITION_OPTIONS}
                                        label="position"
                                        tooltip="Border position (box sizing)"
                                        handleOnChange={handleOnChange}
                                    />
                                </Box>
                                <Box flex={1} />
                            </Box>
                        </PropertyRow>
                    )}
                </Box>
            }
        />
    )
}

export default Border
